// src/pages/tutorials/TutorialCustomPreprocessing.jsx
import React, { Component, Fragment } from "react";
import { Helmet } from 'react-helmet';
import HeaderOne from '../../components/header/HeaderOne'; // Adjusted path
import Footer from '../../components/footer/Footer'; // Adjusted path
import ScrollToTop from 'react-scroll-up';
import { FiChevronUp, FiCode, FiSettings, FiUploadCloud } from "react-icons/fi"; // Example icons
import Breadcrumb from "../../components/common/Breadcrumb"; // Adjusted path

class TutorialCustomPreprocessing extends Component {
    render() {
        return (
            <Fragment>
                <Helmet>
                    <title>Custom Preprocessing Scripts | Eidocell Documentation</title>
                    <meta name="description" content="Learn how to use custom Python scripts in Eidocell to preprocess your images upon import, enabling tailored image normalization, enhancement, and artifact removal." />
                </Helmet>

                <HeaderOne />

                <Breadcrumb title={'Custom Python Preprocessing Scripts'} />

                <div className="main-content-area ptb--120 bg_color--1">
                    <div className="container">
                        <div className="row">
                            <div className="col-lg-10 offset-lg-1">
                                <div className="startup-guide-content"> {/* Reusing startup-guide styles */}
                                    <p className="lead">
                                        Eidocell provides powerful built-in tools, but sometimes your images require specific preprocessing steps not covered by default options.
                                        To address this, Eidocell (<em>hypothetically, or if implemented</em>) allows you to integrate custom Python scripts that can be executed on your images during the import or as a dedicated preprocessing step.
                                        This tutorial explains the concept and potential workflow.
                                    </p>
                                    <p><strong>Note:</strong> The availability and exact mechanism for running custom Python scripts are dependent on Eidocell's specific implementation. Always refer to the application's most current documentation or interface for precise instructions.</p>

                                    <section id="why-custom-scripts" className="mb--40">
                                        <h2><FiSettings className="heading-icon" /> Why Use Custom Preprocessing Scripts?</h2>
                                        <p>Custom scripts offer flexibility for tasks such as:</p>
                                        <ul>
                                            <li><strong>Advanced Normalization:</strong> Implementing specific background subtraction, flat-field correction, or intensity scaling algorithms tailored to your imaging setup.</li>
                                            <li><strong>Image Enhancement:</strong> Applying custom filters for sharpening, denoising (e.g., non-local means), or contrast enhancement beyond standard options.</li>
                                            <li><strong>Artifact Removal:</strong> Scripting logic to detect and correct or mask specific imaging artifacts.</li>
                                            <li><strong>Channel Manipulation:</strong> Complex operations on multi-channel images, like custom channel arithmetic or specific color space conversions before feature extraction.</li>
                                            <li><strong>Integration with Specific Libraries:</strong> Utilizing specialized Python image processing libraries (e.g., scikit-image, Mahotas, a custom internal library) that are not directly integrated into Eidocell's core.</li>
                                        </ul>
                                    </section>

                                    <section id="script-requirements" className="mb--40">
                                        <h2><FiCode className="heading-icon" /> Script Requirements and Environment</h2>
                                        <p>
                                            For custom scripts to work seamlessly with Eidocell, they generally need to adhere to certain conventions:
                                        </p>
                                        <ul>
                                            <li>
                                                <strong>Python Environment:</strong> Eidocell might use its own bundled Python environment or expect scripts to be compatible with a system Python environment where necessary libraries (like OpenCV, NumPy, Pillow) are installed. Check Eidocell's documentation for specifics on the Python version and required base packages.
                                            </li>
                                            <li>
                                                <strong>Input/Output:</strong>
                                                <ul>
                                                    <li>Your script will typically receive the path to an input image file.</li>
                                                    <li>It should process the image and save the output (the preprocessed image) to a specified path or return the image data (e.g., as a NumPy array) in a format Eidocell can understand.</li>
                                                    <li>Eidocell might pass additional parameters to your script (e.g., user-defined values from the UI).</li>
                                                </ul>
                                            </li>
                                            <li>
                                                <strong>Script Structure (Example):</strong> A common pattern might involve a main function that takes an image path and output path:
                                                <pre>
{`
import cv2 # OpenCV for image processing
import numpy as np

def preprocess_image(input_image_path, output_image_path, custom_param1=0.5):
    # Load the image
    img = cv2.imread(input_image_path, cv2.IMREAD_UNCHANGED)
    if img is None:
        print(f"Error: Could not load image {input_image_path}")
        return False

    # --- Your Custom Preprocessing Logic Here ---
    # Example: Apply a Gaussian blur
    # blurred_img = cv2.GaussianBlur(img, (5, 5), 0)

    # Example: Simple intensity scaling (using custom_param1)
    processed_img = np.clip(img * custom_param1, 0, 255).astype(np.uint8)
    # --- End Custom Logic ---

    # Save the processed image
    success = cv2.imwrite(output_image_path, processed_img)
    if not success:
        print(f"Error: Could not save processed image to {output_image_path}")
    return success

if __name__ == "__main__":
    # This part is usually for testing your script independently.
    # Eidocell would call the preprocess_image function directly.
    # input_path = "path/to/your/test_image.tif"
    # output_path = "path/to/your/processed_image.tif"
    # if preprocess_image(input_path, output_path, custom_param1=0.75):
    #     print(f"Processed image saved to {output_path}")
    # else:
    #     print("Processing failed.")
`}
</pre>
                                            </li>
                                            <li><strong>Error Handling:</strong> Your script should ideally include basic error handling (e.g., for file not found) and provide informative messages.</li>
                                        </ul>
                                    </section>

                                    <section id="integrating-scripts" className="mb--40">
                                        <h2><FiUploadCloud className="heading-icon" /> Integrating and Using Custom Scripts in Eidocell</h2>
                                        <p>The exact method of integration depends on Eidocell's design. Here are a few possibilities:</p>

                                        <h3>Option 1: Specifying a Script during Session Creation/Image Import</h3>
                                        <p>
                                            When creating a new session or importing images, there might be an "Advanced" or "Preprocessing" section where you can:
                                        </p>
                                        <ol>
                                            <li><strong>Select Script:</strong> Browse and select your custom Python (<code>.py</code>) script file.</li>
                                            <li><strong>Set Parameters:</strong> If your script accepts parameters (like <code>custom_param1</code> in the example), the UI might provide fields to enter these values.</li>
                                            <li>Eidocell would then run this script on each image as it's being added to the session. The script's output would be the version of the image used for all subsequent analysis.</li>
                                        </ol>
                                        <p><code>[SCREENSHOT: Hypothetical "New Session" or "Import Images" dialog with an "Advanced/Preprocessing" tab showing a field to select a Python script and input fields for script parameters.]</code></p>

                                        <h3>Option 2: A Dedicated Preprocessing Module/View</h3>
                                        <p>
                                            Eidocell might have a dedicated "Preprocessing" view or a tool within one of the existing views (like Gallery or Segmentation) where you can:
                                        </p>
                                        <ol>
                                            <li>Select currently loaded images.</li>
                                            <li>Choose a custom Python script.</li>
                                            <li>Configure its parameters.</li>
                                            <li>Run the script on the selected images, potentially creating new (preprocessed) versions of these images or overwriting the originals (with a warning).</li>
                                        </ol>
                                        <p><code>[SCREENSHOT: Placeholder for a dedicated Preprocessing UI in Eidocell, allowing selection of images, a script, and parameters, with a "Run Script" button.]</code></p>
                                        
                                        <h3>Option 3: Configuration via Settings</h3>
                                        <p>
                                            It's also possible that custom scripts are managed globally via the main application Settings, where you could define a list of available preprocessing scripts and their default parameters. Then, during import or analysis, you might select from this predefined list.
                                        </p>

                                        <h3>Managing Script Output</h3>
                                        <p>
                                            Be mindful of where your script saves its output. Eidocell will need to know the location of the processed images to use them. The script might save them to a temporary location, a new "processed_images" subfolder within your session, or overwrite originals (use with caution and backups!).
                                        </p>
                                    </section>

                                    <section id="best-practices-scripts" className="mb--40">
                                        <h2>4. Best Practices for Custom Scripts</h2>
                                        <ul>
                                            <li><strong>Keep it Simple:</strong> Start with simple scripts and add complexity gradually.</li>
                                            <li><strong>Test Independently:</strong> Thoroughly test your Python script outside of Eidocell with sample images to ensure it works as expected and handles edge cases.</li>
                                            <li><strong>Dependencies:</strong> If your script relies on Python libraries not commonly bundled (beyond NumPy, OpenCV, Pillow), ensure those libraries are installed in the Python environment Eidocell uses. This can be a significant challenge for portable applications; containerization (e.g., Docker) is an advanced solution for complex dependencies but likely outside Eidocell's direct scope for user scripts.</li>
                                            <li><strong>Performance:</strong> Be mindful of performance, especially when processing large datasets. Optimize your Python code where possible.</li>
                                            <li><strong>Idempotency (if applicable):</strong> If a script might be run multiple times on the same image, consider if it should produce the same result each time or if it's an incremental process.</li>
                                            <li><strong>Logging:</strong> Add print statements or use Python's `logging` module within your script for debugging. Eidocell might capture and display this output.</li>
                                            <li><strong>Version Control:</strong> Keep your custom scripts under version control (e.g., Git) like any other code.</li>
                                        </ul>
                                    </section>

                                    <section id="next-steps">
                                        <h2>Next Steps</h2>
                                        <p>
                                            Custom preprocessing scripts, when supported, provide immense power to tailor Eidocell to your unique image analysis challenges.
                                            Always consult the specific documentation for your version of Eidocell to understand the exact capabilities and procedures for using custom scripts.
                                        </p>
                                        <p>
                                            After preprocessing, you'll typically proceed with <a href="#/usage/tutorials/basic-segmentation">Segmentation</a> and <a href="#/usage/tutorials/feature-extraction">Feature Extraction</a> on your enhanced images.
                                        </p>
                                    </section>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <Footer />

                <div className="backto-top">
                    <ScrollToTop showUnder={160}>
                        <FiChevronUp />
                    </ScrollToTop>
                </div>
            </Fragment>
        )
    }
}

export default TutorialCustomPreprocessing;